import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Globe, Sparkles, HeartPulse, Dumbbell, GraduationCap, Car, Building2, Briefcase, LayoutGrid } from 'lucide-react';

const VERTICAL_ICONS = {
  beauty: Sparkles, health: HeartPulse, fitness: Dumbbell,
  education: GraduationCap, automotive: Car, real_estate: Building2,
  professional_services: Briefcase, generic: LayoutGrid,
};

export default function Verticals() {
  const [verticals, setVerticals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get('/admin/verticals').then(r => setVerticals(r.data)).catch(() => {});
  }, []);

  const loadDetail = async (key) => {
    setSelected(key);
    try {
      const { data } = await api.get(`/admin/verticals/${key}`);
      setDetail(data);
    } catch {}
  };

  return (
    <div data-testid="verticals-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight">Verticales</h1>
        <p className="text-sm text-[#64748B] mt-1">Catálogo de verticales soportadas por la plataforma</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-3">
          {verticals.map(v => {
            const Icon = VERTICAL_ICONS[v.key] || Globe;
            return (
              <button
                key={v.key}
                onClick={() => loadDetail(v.key)}
                className={`w-full text-left p-4 rounded-xl border transition-all hover:-translate-y-0.5 ${
                  selected === v.key
                    ? 'bg-white border-[#F72585] shadow-sm'
                    : 'bg-white border-[#E2E8F0] hover:border-[#CBD5E1]'
                }`}
                data-testid={`vertical-card-${v.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selected === v.key ? 'bg-[#F72585]/10' : 'bg-[#F8F9FA]'}`}>
                    <Icon className={`w-5 h-5 ${selected === v.key ? 'text-[#F72585]' : 'text-[#64748B]'}`} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{v.name}</p>
                    <p className="text-xs text-[#64748B]">{v.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-7">
          {detail ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 sticky top-6" data-testid="vertical-detail">
              <h2 className="text-xl font-bold text-[#0F172A] tracking-tight mb-1">{detail.name}</h2>
              <p className="text-sm text-[#64748B] mb-6">{detail.description}</p>

              <div className="space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">Subtipos</h4>
                  <div className="flex flex-wrap gap-2">
                    {detail.subtypes?.map(s => (
                      <span key={s} className="text-xs px-2.5 py-1 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0]">{s}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">Diccionario</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(detail.dictionary || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-sm">
                        <span className="text-[#64748B]">{k}:</span>
                        <span className="text-[#0F172A] font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">Pipeline por defecto</h4>
                  <div className="flex flex-wrap gap-2">
                    {detail.default_pipeline_stages?.map((s, i) => (
                      <div key={s} className="flex items-center gap-1">
                        <span className="text-xs px-2.5 py-1 bg-[#0F172A] text-white rounded-full">{i + 1}</span>
                        <span className="text-sm text-[#0F172A]">{s}</span>
                        {i < detail.default_pipeline_stages.length - 1 && <span className="text-[#CBD5E1] mx-1">&rarr;</span>}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">Módulos</h4>
                  <div className="flex flex-wrap gap-2">
                    {detail.modules?.map(m => (
                      <span key={m} className="text-xs px-2.5 py-1 bg-[#F72585]/10 text-[#F72585] rounded-full border border-[#F72585]/20">{m}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">Roles sugeridos</h4>
                  <div className="flex flex-wrap gap-2">
                    {detail.default_roles?.map(r => (
                      <span key={r} className="text-xs px-2.5 py-1 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0] capitalize">{r.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 flex flex-col items-center justify-center text-[#64748B]">
              <Globe className="w-12 h-12 mb-3 text-[#E2E8F0]" />
              <p className="text-sm">Selecciona una vertical para ver su detalle</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
